import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { countsInTotals } from '../utils/timesheets'
import type {
  ClockState,
  GeoPoint,
  TimeEntry,
  TimeEntrySource,
  ClientRate,
  MemberRate,
  TimeEntryBucket,
  TimesheetWeek,
  ApprovalWeekGroup,
  QueueTimeEntry,
  ListUnpostedLabourInput,
  ListUnpostedLabourResult,
  PostPendingLabourInput,
  PostPendingLabourResult,
  BackfillActualMinutesInput,
  BackfillActualMinutesResult,
} from '../types/timesheets'
import type { HelperMutationRecord, TimeClockPayload } from './helper/helperOutboxDb'

export interface StartClockInput {
  workspaceId: string
  clientGeneratedId?: string
  kind?: 'CLOCK' | 'TIMER'
  nodeId?: string | null
  geoIn?: GeoPoint | null
  device?: string | null
  occurredAt?: string
  tz?: string
}

export interface StartClockResult {
  success: boolean
  clock: ClockState
  stoppedClock?: {
    id: string
    workspaceId: string
    uid: string
    nodeId?: string | null
    start: string
    end: string
    minutes: number
    source: string
    stoppedAt: string
  } | null
}

export interface StopClockInput {
  workspaceId: string
  geoOut?: GeoPoint | null
  note?: string | null
  billable?: boolean
  clientGeneratedId?: string
  occurredAt?: string
  tz?: string
}

export interface StopClockResult {
  success: boolean
  entry: TimeEntry | null
}

export interface UpsertTimeEntryInput {
  workspaceId: string
  entry: {
    id?: string
    nodeId?: string | null
    start: string
    end: string
    minutes?: number
    source?: TimeEntrySource
    sourceRef?: string | null
    note?: string | null
    billable?: boolean
    clientId?: string | null
    tz?: string
  }
}

export interface UpsertTimeEntryResult {
  success: boolean
  id: string
  entry: TimeEntry
}

export interface SubmitWeekInput {
  workspaceId: string
  weekId: string
}

export interface SubmitWeekResult {
  success: boolean
  count: number
}

const REGION = 'asia-south1'

function getClientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export async function startClock(input: StartClockInput): Promise<StartClockResult> {
  const payload: StartClockInput = {
    tz: input.tz ?? getClientTimeZone(),
    ...input,
  }
  return callable<StartClockInput, StartClockResult>('startClock', payload, { region: REGION })
}

export async function stopClock(input: StopClockInput): Promise<StopClockResult> {
  const payload: StopClockInput = {
    tz: input.tz ?? getClientTimeZone(),
    ...input,
  }
  return callable<StopClockInput, StopClockResult>('stopClock', payload, { region: REGION })
}

export async function upsertTimeEntry(input: UpsertTimeEntryInput): Promise<UpsertTimeEntryResult> {
  const payload: UpsertTimeEntryInput = {
    workspaceId: input.workspaceId,
    entry: {
      tz: input.entry.tz ?? getClientTimeZone(),
      ...input.entry,
    },
  }
  return callable<UpsertTimeEntryInput, UpsertTimeEntryResult>('upsertTimeEntry', payload, { region: REGION })
}

export async function submitWeek(input: SubmitWeekInput): Promise<SubmitWeekResult> {
  return callable<SubmitWeekInput, SubmitWeekResult>('submitWeek', input, { region: REGION })
}

export interface ResolveAutoTimeDraftInput {
  workspaceId: string
  entryId: string
  action: 'KEEP' | 'DELETE'
}

export interface ResolveAutoTimeDraftResult {
  success: boolean
  action: 'KEEP' | 'DELETE'
}

export async function resolveAutoTimeDraft(
  input: ResolveAutoTimeDraftInput,
): Promise<ResolveAutoTimeDraftResult> {
  return callable<ResolveAutoTimeDraftInput, ResolveAutoTimeDraftResult>(
    'resolveAutoTimeDraft',
    input,
    { region: REGION },
  )
}

export interface RecordFocusSessionInput {
  workspaceId: string
  clientGeneratedId: string
  startedAt: string
  endedAt: string
  targetId?: string | null
  targetKind?: string | null
}

export interface RecordFocusSessionResult {
  saved: boolean
  id?: string
  draftCreated?: boolean
}

export async function recordFocusSession(
  input: RecordFocusSessionInput,
): Promise<RecordFocusSessionResult> {
  return callable<RecordFocusSessionInput, RecordFocusSessionResult>(
    'recordFocusSession',
    input,
    { region: REGION },
  )
}

export interface ReviewWeekInput {
  workspaceId: string
  targetUid: string
  weekId: string
  decision: 'APPROVE' | 'REJECT'
  note?: string
}

export interface ReviewWeekResult {
  success: boolean
  decision: 'APPROVE' | 'REJECT'
  count: number
}

export interface ReopenWeekInput {
  workspaceId: string
  targetUid: string
  weekId: string
  reason: string
}

export interface ReopenWeekResult {
  success: boolean
  count: number
}

export interface ListApprovalQueueInput {
  workspaceId: string
  status?: 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  weekId?: string
  cursor?: string | null
  limit?: number
}

export interface ListApprovalQueueResult {
  success: boolean
  weeks: ApprovalWeekGroup[]
  nextCursor: string | null
}

export async function reviewWeek(input: ReviewWeekInput): Promise<ReviewWeekResult> {
  return callable<ReviewWeekInput, ReviewWeekResult>('reviewWeek', input, { region: REGION })
}

export async function reopenWeek(input: ReopenWeekInput): Promise<ReopenWeekResult> {
  return callable<ReopenWeekInput, ReopenWeekResult>('reopenWeek', input, { region: REGION })
}

export async function listApprovalQueue(input: ListApprovalQueueInput): Promise<ListApprovalQueueResult> {
  return callable<ListApprovalQueueInput, ListApprovalQueueResult>('listApprovalQueue', input, { region: REGION })
}

export interface ListTeamClockBoardInput {
  workspaceId: string
}

export interface TeamClockBoardRow {
  uid: string
  displayName: string
  photoURL: string
  nodeId: string | null
  nodeTitle: string
  kind: string
  startedAt: string
  geoIn?: GeoPoint
}

export interface ListTeamClockBoardResult {
  success: boolean
  rows: TeamClockBoardRow[]
}

export async function listTeamClockBoard(
  input: ListTeamClockBoardInput,
): Promise<ListTeamClockBoardResult> {
  return callable<ListTeamClockBoardInput, ListTeamClockBoardResult>(
    'listTeamClockBoard',
    input,
    { region: REGION },
  )
}

export interface SetMemberExpectedHoursInput {
  workspaceId: string
  uid: string
  hours: number | null
}

export interface SetMemberExpectedHoursResult {
  success: boolean
  expectedWeeklyMinutes: number | null
}

export async function setMemberExpectedHours(
  input: SetMemberExpectedHoursInput,
): Promise<SetMemberExpectedHoursResult> {
  return callable<SetMemberExpectedHoursInput, SetMemberExpectedHoursResult>(
    'setMemberExpectedHours',
    input,
    { region: REGION },
  )
}

export type TeamReportTemplate =
  | 'WEEKLY_HOURS'
  | 'BILLABLE_SUMMARY'
  | 'PLANNED_VS_ACTUAL'
  | 'UTILISATION'

export interface GetTeamTimeReportInput {
  workspaceId: string
  from: string
  to: string
  template: TeamReportTemplate
  includeSubmitted?: boolean
}

export interface WeeklyHoursRow {
  uid: string
  displayName: string
  dailyHours: Record<string, number>
  totalHours: number
  submitted?: boolean
}

export interface BillableSummaryRow {
  uid: string
  displayName: string
  nodeId: string | null
  nodeTitle: string
  billableHours: number
  nonBillableHours: number
  totalHours: number
  billableShare: number
}

export interface PlannedVsActualRow {
  id: string
  title: string
  plannedHours: number
  actualHours: number
  variance: number
}

export interface UtilisationRow {
  uid: string
  displayName: string
  totalHours: number
  expectedHours: number
  utilisation: number
  billableShare: number
  byNode: Array<{
    nodeId: string | null
    nodeTitle: string
    hours: number
    share: number
  }>
}

export type TeamReportRow =
  | WeeklyHoursRow
  | BillableSummaryRow
  | PlannedVsActualRow
  | UtilisationRow

export interface GetTeamTimeReportResult {
  success: boolean
  template: TeamReportTemplate
  from: string
  to: string
  rows: TeamReportRow[]
}

export async function getTeamTimeReport(
  input: GetTeamTimeReportInput,
): Promise<GetTeamTimeReportResult> {
  return callable<GetTeamTimeReportInput, GetTeamTimeReportResult>(
    'getTeamTimeReport',
    input,
    { region: REGION },
  )
}


export type {
  TimeEntry,
  ClockState,
  TimesheetWeek,
  ApprovalWeekGroup,
  QueueTimeEntry,
}

export function subscribeMyTimesheetWeek(
  workspaceId: string,
  uid: string,
  weekId: string,
  onChange: (week: TimesheetWeek | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !uid || !weekId) {
    onChange(null)
    return () => {}
  }
  const docRef = doc(firestoreDb, 'timesheet_weeks', `${uid}_${weekId}`)
  return onSnapshot(
    docRef,
    (snap) => {
      if (!snap.exists()) {
        onChange(null)
        return
      }
      onChange({ id: snap.id, ...(snap.data() as Omit<TimesheetWeek, 'id'>) })
    },
    (err) => {
      onError?.(err)
    },
  )
}

export function subscribeMyWeekEntries(
  workspaceId: string,
  uid: string,
  weekId: string,
  onChange: (entries: TimeEntry[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(firestoreDb, 'time_entries'),
    where('workspaceId', '==', workspaceId),
    where('uid', '==', uid),
    where('weekId', '==', weekId),
  )

  return onSnapshot(
    q,
    (snap) => {
      const entries: TimeEntry[] = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<TimeEntry, 'id'>),
      }))
      onChange(entries)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeMyClockState(
  workspaceId: string,
  uid: string,
  onChange: (clock: ClockState | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  try {
    const docRef = doc(firestoreDb, 'time_clock_state', `${workspaceId}_${uid}`)

    return onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          onChange(null)
          return
        }
        const data = snap.data() as ClockState
        onChange(data)
      },
      (err) => {
        if (onError) onError(err)
      },
    )
  } catch (err) {
    if (onError) onError(err as Error)
    return () => {}
  }
}

export function subscribeMyNodeMinutes(
  workspaceId: string,
  uid: string,
  nodeId: string,
  onChange: (totalMinutes: number) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !uid || !nodeId) {
    onChange(0)
    return () => {}
  }

  let loggedMinutes = 0
  let runningElapsedMinutes = 0
  let clockUnsub: Unsubscribe | null = null
  let tickInterval: ReturnType<typeof setInterval> | null = null

  const emit = () => {
    onChange(loggedMinutes + runningElapsedMinutes)
  }

  try {
    const entriesQuery = query(
      collection(firestoreDb, 'time_entries'),
      where('workspaceId', '==', workspaceId),
      where('uid', '==', uid),
      where('nodeId', '==', nodeId),
    )

    const entriesUnsub = onSnapshot(
      entriesQuery,
      (snap) => {
        let sum = 0
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() as Partial<TimeEntry>
          // Sum minutes of entries that count in totals (excludes REJECTED and unconfirmed overlaps)
          if (countsInTotals(data as TimeEntry)) {
            sum += typeof data.minutes === 'number' ? data.minutes : 0
          }
        })
        loggedMinutes = Math.round(sum)
        emit()
      },
      (err) => {
        if (onError) onError(err)
      },
    )

    const updateRunningElapsed = (clock: ClockState | null) => {
      if (clock && clock.kind === 'TIMER' && clock.nodeId === nodeId && clock.startedAt) {
        const startMs = new Date(clock.startedAt).getTime()
        if (!isNaN(startMs)) {
          const elapsedMs = Math.max(0, Date.now() - startMs)
          runningElapsedMinutes = Math.floor(elapsedMs / 60000)
        } else {
          runningElapsedMinutes = 0
        }
      } else {
        runningElapsedMinutes = 0
      }
    }

    clockUnsub = subscribeMyClockState(
      workspaceId,
      uid,
      (clock) => {
        if (tickInterval) {
          clearInterval(tickInterval)
          tickInterval = null
        }
        updateRunningElapsed(clock)
        emit()

        if (clock && clock.kind === 'TIMER' && clock.nodeId === nodeId && clock.startedAt) {
          tickInterval = setInterval(() => {
            updateRunningElapsed(clock)
            emit()
          }, 1000)
        }
      },
      (err) => {
        if (onError) onError(err)
      },
    )

    return () => {
      entriesUnsub()
      if (clockUnsub) clockUnsub()
      if (tickInterval) clearInterval(tickInterval)
    }
  } catch (err) {
    if (onError) onError(err as Error)
    return () => {}
  }
}

export async function queueTimeClockMutation(
  payload: TimeClockPayload,
): Promise<HelperMutationRecord> {
  const { getSharedDeviceWorkRouterContext, routeTimeClockMutation } = await import('./sharedDeviceWorkRouter')
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeTimeClockMutation(payload, routerCtx)
  }
  const { putMutation } = await import('./helper/helperOutboxDb')
  const now = Date.now()
  const record: HelperMutationRecord = {
    id: payload.clientGeneratedId || `tc_${now}_${Math.random().toString(36).slice(2, 8)}`,
    type: 'TIME_CLOCK',
    payload,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
  }
  await putMutation(record)
  return record
}

export const TIME_CLOCK_SERVER_REJECTION_CODES: ReadonlySet<string> = new Set([
  'invalid-argument',
  'failed-precondition',
  'permission-denied',
  'not-found',
  'already-exists',
  'unauthenticated',
])

export function extractCallableErrorCode(error: unknown): string {
  if (typeof error === 'object' && error && 'code' in error && typeof (error as { code: unknown }).code === 'string') {
    return (error as { code: string }).code.replace(/^functions\//, '').toLowerCase()
  }
  return ''
}

export function isTimeClockServerRejection(error: unknown): boolean {
  const code = extractCallableErrorCode(error)
  return TIME_CLOCK_SERVER_REJECTION_CODES.has(code)
}

export function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true
  }
  if (!error) {
    return false
  }
  // Server rejections are definitive business/security failures, not network drops
  if (isTimeClockServerRejection(error)) {
    return false
  }
  const code = extractCallableErrorCode(error)
  if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'internal') {
    return true
  }
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase()
  return (
    msg.includes('network') ||
    msg.includes('offline') ||
    msg.includes('failed to fetch') ||
    msg.includes('unavailable') ||
    msg.includes('timeout') ||
    msg.includes('load failed')
  )
}

export interface SetMemberRateInput {
  workspaceId: string
  targetUid: string
  costRate: number
  billRate?: number | null
  currency: string
  effectiveFrom: string
}

export interface SetMemberRateResult {
  success: boolean
  rate: MemberRate
}

export interface SetClientRateInput {
  workspaceId: string
  clientId: string
  rate: number
  currency: string
  effectiveFrom: string
}

export interface SetClientRateResult {
  success: boolean
  rate: ClientRate
  clientRate: {
    rate: number
    currency: string
    effectiveFrom: string
  }
}

export interface SetTimeEntryBucketInput {
  workspaceId: string
  entryId: string
  bucket: TimeEntryBucket
}

export interface SetTimeEntryBucketResult {
  success: boolean
  entryId: string
  bucket: TimeEntryBucket
  bucketSource: 'OVERRIDE'
}

export interface BackfillTimeEntryBucketsInput {
  workspaceId: string
  limit?: number
  cursor?: string | null
}

export interface BackfillTimeEntryBucketsResult {
  success: boolean
  updated: number
  skipped: number
  nextCursor: string | null
}

export async function setMemberRate(input: SetMemberRateInput): Promise<SetMemberRateResult> {
  return callable<SetMemberRateInput, SetMemberRateResult>('setMemberRate', input, { region: REGION })
}

export async function setClientRate(input: SetClientRateInput): Promise<SetClientRateResult> {
  return callable<SetClientRateInput, SetClientRateResult>('setClientRate', input, { region: REGION })
}

export async function setTimeEntryBucket(input: SetTimeEntryBucketInput): Promise<SetTimeEntryBucketResult> {
  return callable<SetTimeEntryBucketInput, SetTimeEntryBucketResult>('setTimeEntryBucket', input, { region: REGION })
}

export async function backfillTimeEntryBuckets(input: BackfillTimeEntryBucketsInput): Promise<BackfillTimeEntryBucketsResult> {
  return callable<BackfillTimeEntryBucketsInput, BackfillTimeEntryBucketsResult>('backfillTimeEntryBuckets', input, { region: REGION })
}

export async function listUnpostedLabour(input: ListUnpostedLabourInput): Promise<ListUnpostedLabourResult> {
  return callable<ListUnpostedLabourInput, ListUnpostedLabourResult>('listUnpostedLabour', input, { region: REGION })
}

export async function postPendingLabour(input: PostPendingLabourInput): Promise<PostPendingLabourResult> {
  return callable<PostPendingLabourInput, PostPendingLabourResult>('postPendingLabour', input, { region: REGION })
}

export async function backfillActualMinutes(input: BackfillActualMinutesInput): Promise<BackfillActualMinutesResult> {
  return callable<BackfillActualMinutesInput, BackfillActualMinutesResult>('backfillActualMinutes', input, { region: REGION })
}

export function subscribeClientRates(
  workspaceId: string,
  clientId: string,
  callback: (rate: ClientRate | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const docRef = doc(firestoreDb, 'client_rates', `${workspaceId}_${clientId}`)
  return onSnapshot(
    docRef,
    (snap) => {
      if (!snap.exists()) {
        callback(null)
        return
      }
      callback(snap.data() as ClientRate)
    },
    (err) => {
      onError?.(err)
    },
  )
}

export function subscribeMemberRates(
  workspaceId: string,
  targetUid: string,
  callback: (rate: MemberRate | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const docRef = doc(firestoreDb, 'member_rates', `${workspaceId}_${targetUid}`)
  return onSnapshot(
    docRef,
    (snap) => {
      if (!snap.exists()) {
        callback(null)
        return
      }
      callback(snap.data() as MemberRate)
    },
    (err) => {
      onError?.(err)
    },
  )
}

export interface ExportBillableHoursInput {
  workspaceId: string
  clientId?: string
  from?: string
  to?: string
  format: 'CSV' | 'XLSX'
}

export interface ExportBillableHoursResult {
  success: boolean
  format: 'CSV' | 'XLSX'
  filename: string
  downloadUrl: string
  rowCount: number
  grandTotalMinutes: number
  grandTotalHours: number
  grandTotalAmountMinor: number
  currency: string
  truncated: boolean
}

export async function exportBillableHours(
  input: ExportBillableHoursInput,
): Promise<ExportBillableHoursResult> {
  return callable<ExportBillableHoursInput, ExportBillableHoursResult>(
    'exportBillableHours',
    input,
    { region: REGION },
  )
}

export interface BackfillTimeEntryZonesInput {
  workspaceId: string
}

export interface BackfillTimeEntryZonesResult {
  success: boolean
  updatedCount: number
  unmovedCount: number
  touchedWeeksCount: number
  unmovedWeeksCount: number
}

export async function backfillTimeEntryZones(
  input: BackfillTimeEntryZonesInput,
): Promise<BackfillTimeEntryZonesResult> {
  return callable<BackfillTimeEntryZonesInput, BackfillTimeEntryZonesResult>(
    'backfillTimeEntryZones',
    input,
    { region: REGION },
  )
}

export interface ExportPayrollHoursInput {
  workspaceId: string
  from?: string | Date | number
  to?: string | Date | number
  format?: 'CSV' | 'XLSX'
  uid?: string
}

export interface ExportPayrollHoursResult {
  success: boolean
  downloadUrl: string
  filename: string
  format: 'CSV' | 'XLSX'
  rowCount: number
  storagePath: string
  truncated: boolean
}

export async function exportPayrollHours(
  input: ExportPayrollHoursInput,
): Promise<ExportPayrollHoursResult> {
  return callable<ExportPayrollHoursInput, ExportPayrollHoursResult>(
    'exportPayrollHours',
    input,
    { region: REGION },
  )
}

